import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { pickupStoreLinkState } from '~/services/pickupStoreLinkState';
import { mainStore, userStore } from '~/stores';

/**
 * Whether the delivery address modal must be shown as a blocking flow the
 * user cannot dismiss without picking an address. True only while the
 * company opts into it (`autoOpenAddressModalOnFirstVisit`), stores are
 * synchronized, no address is selected yet, RN onboarding (which owns
 * its own first-run flow) isn't still pending, and no in-store QR link is
 * mid-flight — that link is about to supply the very address this would ask
 * for, and asking anyway is the screen the link exists to skip.
 */
export function getIsMandatoryAddressSelection(): boolean {
  return (
    company.autoOpenAddressModalOnFirstVisit &&
    mainStore.isAllStoresSynchronized &&
    !userStore.deliveryAddress &&
    !(mainStore.isRN && !mainStore.onboardingSeen) &&
    !pickupStoreLinkState.isPending
  );
}

/**
 * Which modal type implements the address-selection flow for the current
 * company: the external pickup-point list for companies without delivery
 * zones, otherwise the delivery/pickup address picker.
 */
export function getMandatoryAddressModalType(): ModalType {
  return company.isExternalPickupPoints
    ? ModalType.DeliveryAddress
    : ModalType.EditAddressLocation;
}

/**
 * Modal types that belong to the address-selection flow. While the mandatory
 * gate is active these are allowed to stay open (the user is progressing
 * through address selection); any other `modal` value is treated as an escape
 * attempt and gets replaced with the mandatory address modal.
 */
const MANDATORY_ADDRESS_FLOW_MODAL_TYPES: ReadonlySet<ModalType> = new Set([
  ModalType.EditAddressLocation,
  ModalType.DeliveryAddress,
  ModalType.DeliveryAddressList,
  ModalType.EditAddressDetails,
  ModalType.OutOfZone,
]);

/**
 * Whether the given `modal` query-param value belongs to the mandatory
 * address-selection flow. Unknown or unrelated modal values (including `null`)
 * return `false`, so the blocking gate re-opens the address modal over them.
 */
export function isMandatoryAddressFlowModal(modal: string | null): boolean {
  return !!modal && MANDATORY_ADDRESS_FLOW_MODAL_TYPES.has(modal as ModalType);
}
